'use client';

import type { ComponentProps, MouseEvent } from 'react';
import { useOrderTransition } from '@/components/transition/OrderTransition';
import { Button } from '@/components/ui/Button';
import { path } from '@/i18n/routing';
import { useLocale } from '@/i18n/useLocale';

type ButtonLinkProps = Extract<ComponentProps<typeof Button>, { href: string }>;

type OrderCtaProps = Omit<ButtonLinkProps, 'href' | 'onClick'> & {
  href?: string;
};

/**
 * The order CTA — renders as a real link to the ariza form, but a plain
 * left-click is upgraded to the curtain transition. Modified clicks
 * (new tab, download) and no-JS visitors keep native link behaviour.
 */
export function OrderCta({ href, ...props }: OrderCtaProps) {
  const startOrder = useOrderTransition();
  const lang = useLocale();
  /* Every caller passes an href today; the fallback keeps the component honest
     if one ever does not, and it has to be language-aware to be a real link. */
  const target = href ?? path(lang, 'contact', 'ariza');

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (
      !startOrder ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    event.preventDefault();
    startOrder();
  };

  return <Button href={target} onClick={handleClick} {...props} />;
}
